import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import api from '../services/api';
import { refreshPermissionCache, userHasPermission } from '../services/permissionService';
import { getTokenAuth, removeTokenAuth, setTokenAuth } from '../utils/authUtils';
import i18n from '../i18n';
import {
  AUTH_SESSION_SYNC_EVENT,
  AUTH_UNAUTHORIZED_EVENT,
  notifyManualLogout,
  scheduleLoginRedirect,
  subscribeToAuthSessionEvents
} from '../utils/authSessionManager';
const AuthContext = createContext();
// Utiliser console pour le logging frontend
const logger = {
  info: (message, ...args) => console.log(`%c[AuthContext] [INFO] ${message}`, 'color: blue;', ...args),
  debug: (message, ...args) => console.log(`%c[AuthContext] [DEBUG] ${message}`, 'color: gray;', ...args),
  warn: (message, ...args) => console.warn(`%c[AuthContext] [WARN] ${message}`, 'color: orange;', ...args),
  error: (message, ...args) => console.error(`%c[AuthContext] [ERROR] ${message}`, 'color: red;', ...args),
};
export const useAuth = () => useContext(AuthContext);

// Fonction helper pour extraire la langue préférée de l'utilisateur
const getUserPreferredLanguage = (user) => {
  if (!user) return null;
  try {
    const prefs = user.preferences;
    if (typeof prefs === 'string') {
      const parsed = JSON.parse(prefs);
      return parsed?.display?.language || parsed?.language || null;
    }
    return prefs?.display?.language || prefs?.language || null;
  } catch (error) {
    logger.warn('Failed to extract user language preferences:', error);
    return null;
  }
};

// Fonction helper pour basculer la langue i18n de l'utilisateur
const switchUserLanguage = async (user) => {
  const preferredLang = getUserPreferredLanguage(user);
  if (preferredLang && i18n.language !== preferredLang) {
    try {
      await i18n.changeLanguage(preferredLang);
      logger.info(`Language switched to user preference: ${preferredLang}`);
    } catch (error) {
      logger.warn(`Failed to switch language to ${preferredLang}:`, error);
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true); // Initial loading state
  const [authLoading, setAuthLoading] = useState(false); // Loading for auth actions
  const [error, setError] = useState(null);
  const [requiresEmailVerification, setRequiresEmailVerification] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState('');
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Nouvelle garde pour éviter les cycles

  const resetAuthState = (reason = 'unknown') => {
    logger.warn(`Resetting auth state (${reason})`);
    removeTokenAuth();
    localStorage.removeItem('userData');
    localStorage.removeItem('userExplicitCurrencyChoice');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPermissionsLoaded(false);
    setRequiresEmailVerification(false);
    setEmailToVerify('');
    setError(null);
  };

  // Écouter l'événement 401 émis par l'intercepteur axios
  // Quand le token expire ou est invalide : nettoyer l'état React → ProtectedRoute redirige
  useEffect(() => {
    const handleUnauthorized = () => {
      logger.warn('Événement auth:unauthorized reçu - nettoyage de la session');
      resetAuthState('unauthorized-event');
      // Safety net: if ProtectedRoute doesn't redirect within 500ms, force it
      scheduleLoginRedirect();
    };

    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []); // setters useState sont stables, empty deps correct

  useEffect(() => {
    const unsubscribe = subscribeToAuthSessionEvents((event) => {
      if (!event?.type) {
        return;
      }

      if (event.type === 'logout' || event.type === 'unauthorized') {
        logger.warn(`Auth session sync reçu depuis un autre onglet: ${event.type}`);
        window.dispatchEvent(new CustomEvent(AUTH_SESSION_SYNC_EVENT, { detail: event }));
        resetAuthState(`cross-tab-${event.type}`);
        scheduleLoginRedirect();
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    // Éviter les re-chargements multiples
    if (isInitialized) {
      logger.debug('AuthContext already initialized, skipping reload');
      return;
    }

    const loadStoredData = async () => {
      logger.info('useEffect: Loading stored auth data...');
      setLoading(true);
      try {
        const storedToken = getTokenAuth();
        const storedUser = localStorage.getItem('userData');

        if (storedToken && storedUser) {
          logger.debug('Stored token and user found.');
          const userData = JSON.parse(storedUser);

          // Amélioration: Vérifier si l'email est vérifié au chargement
          if (userData && !userData.isEmailVerified && userData.email) {
             logger.warn(`User ${userData.email} loaded from storage requires email verification.`);
             // Ne pas définir le token/user pour forcer la vérification ou une nouvelle connexion
             // Nettoyer au cas où pour éviter un état incohérent
             removeTokenAuth();
             localStorage.removeItem('userData');
             setToken(null); setUser(null); setPermissionsLoaded(false);
             setRequiresEmailVerification(true); // Indiquer qu'une action est requise
             setEmailToVerify(userData.email);
             delete api.defaults.headers.common['Authorization'];
          } else if (userData) {
            // L'utilisateur est vérifié ou le statut n'est pas pertinent/disponible
            logger.info(`User ${userData.email} loaded from storage is verified or status ok.`);
            setToken(storedToken);
            setUser(userData);
            api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
            // Basculer la langue i18n selon la préférence de l'utilisateur
            await switchUserLanguage(userData);
            try {
              await refreshPermissionCache();
              setPermissionsLoaded(true);
            } catch (permError) {
               logger.warn('Failed to refresh permissions on initial load.', permError);
               setPermissionsLoaded(false); // Indiquer que les permissions ne sont pas chargées
               // Optionnel: Déconnecter si les permissions sont cruciales ?
               // logout();
            }
            setRequiresEmailVerification(false); // Assurer que c'est false
            setEmailToVerify('');
          } else {
             logger.warn('Stored user data found but could not be parsed or is invalid.');
             logout();
          }
        } else {
           logger.info('No stored token/user found.');
           setToken(null); setUser(null); setPermissionsLoaded(false);
           setRequiresEmailVerification(false); setEmailToVerify('');
        }
      } catch (e) {
        logger.error('Failed to load or parse auth data from storage', e);
        logout();
      } finally {
        setLoading(false);
        setIsInitialized(true); // Marquer comme initialisé
        logger.info('useEffect: Finished loading stored auth data.');
      }
    };
    loadStoredData();
  }, []); // Dépendance vide stricte

  // --- NOUVELLE FONCTION ---
  /**
   * Met à jour l'état d'authentification complet à partir de données utilisateur et d'un token.
   * Appelée après une connexion réussie OU après une vérification d'email réussie (si le backend renvoie user/token).
   * @param {object} updatedUser - L'objet utilisateur mis à jour.
   * @param {string} newToken - Le nouveau token JWT.
   */
  const updateAuthState = async (updatedUser, newToken) => {
    logger.info('updateAuthState called.');
    try {
      setTokenAuth(newToken); // Stocker via authUtils
      localStorage.setItem('userData', JSON.stringify(updatedUser)); // Stocker user data
      
      // Déclencher un événement personnalisé pour notifier le CurrencyContext (avec délai)
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('userDataUpdated'));
      }, 50); // Petit délai pour éviter les cycles immédiats
      
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`; // Configurer header API
      setToken(newToken); // Mettre à jour état React token
      setUser(updatedUser); // Mettre à jour état React user
      
      // Basculer la langue i18n selon la préférence de l'utilisateur
      await switchUserLanguage(updatedUser);
      
      setRequiresEmailVerification(false); // Assurer que la vérification n'est plus requise
      setEmailToVerify(''); // Nettoyer l'email à vérifier
      await refreshPermissionCache(); // Rafraîchir les permissions
      setPermissionsLoaded(true);
      setError(null); // Nettoyer les erreurs précédentes
      logger.info('Core auth state updated successfully.');
    } catch (error) {
        logger.error('Error during updateAuthState:', error);
        setError('Failed to update authentication session.');
        logout(); // Déconnecter si la mise à jour échoue
        throw error; // Propager l'erreur
    }
  };
  // --- FIN NOUVELLE FONCTION ---
  const login = async (email, password) => {
    logger.info(`Attempting login for email: ${email}`);
    try {
      setAuthLoading(true);
      setError(null);
      setRequiresEmailVerification(false); setEmailToVerify('');

      const response = await api.post('/auth/login', { email, password });
      logger.debug('Login API response received:', response.data);

      if (response.data.requiresEmailVerification) {
        logger.warn(`Login requires email verification for ${email}.`);
        setRequiresEmailVerification(true);
        setEmailToVerify(response.data.email || email);
        // Ne pas définir token/user ici
        setToken(null); setUser(null); setPermissionsLoaded(false);
        delete api.defaults.headers.common['Authorization'];
        return { success: false, requiresVerification: true, email: response.data.email || email, message: response.data.message };
      }

      // Si la vérification n'est PAS requise
      const { token: loginToken, user: basicUser } = response.data;
      logger.info('Login successful, verification not required. Fetching full user details...');
      api.defaults.headers.common['Authorization'] = `Bearer ${loginToken}`; // Définir pour l'appel /me

      const userDetailsResponse = await api.get('/auth/me');
      const fullUserData = userDetailsResponse.data.user;
      logger.debug('Full user details fetched:', fullUserData);

      // Utiliser la fonction centralisée pour mettre à jour l'état
      await updateAuthState(fullUserData, loginToken);
      logger.info('Login successful and auth state updated.');
      return { success: true };

    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.msg || 'Login failed';
      logger.error(`Login failed for ${email}: ${message}`, error.response || error);
      setError(message);

      return { success: false, error: message, code: error.response?.data?.code };
    } finally {
      setAuthLoading(false);
    }
  };

  // --- register (avec catch corrigé) ---
  const register = async (userData) => {
    const emailForLog = userData?.email || 'N/A';
    logger.info(`Attempting registration for email: ${emailForLog}`);
    try {
      setAuthLoading(true);
      setError(null);
      setRequiresEmailVerification(false);
      setEmailToVerify('');

      const endpoint = userData.isOwner ? '/auth/register-owner' : '/auth/register-tenant';
      logger.debug(`Registering using endpoint: ${endpoint}`);

      const response = await api.post(endpoint, userData);
      logger.info('Registration API call successful:', response.data);

      logger.warn(`Registration for ${emailForLog} successful, proceeding to email verification step.`);
      setRequiresEmailVerification(true);
      setEmailToVerify(response.data.user?.email || userData.email);

      setToken(null);
      setUser(null);
      setPermissionsLoaded(false);
      delete api.defaults.headers.common['Authorization'];

      return {
          success: true,
          requiresVerification: true,
          email: response.data.user?.email || userData.email,
          message: response.data.message || 'Inscription réussie. Veuillez vérifier votre email.'
      };

    } catch (error) {
      // Extraire le message d'erreur de la réponse (409 ou autre)
      const message = error.response?.data?.message || error.response?.data?.msg || 'Registration failed';
      logger.error(`Registration failed for ${emailForLog}: ${message}`, error.response || error);
      setError(message); // Mettre à jour l'état d'erreur du contexte (optionnel mais peut être utile)

      // --- CORRECTION : Retourner l'objet d'erreur ---
      return { success: false, error: message };
      // --- FIN CORRECTION ---

    } finally {
      setAuthLoading(false);
    }
  }; // Fin de register

  const logout = () => {
    logger.info('Logging out user.');
    resetAuthState('manual-logout');
    notifyManualLogout({ source: 'auth-context' });
    logger.info('Logout complete.');
  };
  const refreshUserData = async () => {
    try {
      // Vérifier si l'utilisateur est connecté
      const storedToken = getTokenAuth();
      if (!storedToken) return { success: false };
      
      // Configurer le token pour la requête
      api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      
      // Récupérer les données utilisateur à jour
      const response = await api.get('/auth/me');
      const freshUserData = response.data.user;
      
      // Mettre à jour le localStorage et l'état
      localStorage.setItem('userData', JSON.stringify(freshUserData));
      setUser(freshUserData);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données utilisateur:', error);
      return { success: false };
    }
  };

  // Fonction mise à jour pour utiliser les routes existantes du backend
  // --- MODIFIED updateUserProfile ---
  const updateUserProfile = async (profileData) => {
    logger.info('updateUserProfile called with data:', profileData);
    try {
      setLoading(true); // Use general loading state for profile updates
      setError(null);
      let userStateChanged = false; // Flag to track if user state needs refetching

      const trimmedFirstName = profileData.firstName?.trim() || '';
      const trimmedLastName = profileData.lastName?.trim() || '';
      const fullName = [trimmedFirstName, trimmedLastName].filter(Boolean).join(' ').trim();

      // 1. Update basic info (name, email, phone) - Always do this first
      logger.debug('Updating basic profile info...');
      const baseProfileResponse = await api.patch('/auth/update-profile', {
        name: fullName,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        email: profileData.email,
        phone: profileData.phone
      });
      logger.debug('Basic profile update response:', baseProfileResponse.data);
      // Note: We don't update the user state yet, we'll fetch the final state later if needed

      // --- Handle Tenant Role ---
      if (profileData.isTenant && !user.isTenant) { // Activate Tenant
        logger.info("Attempting to activate Tenant role...");
        await api.post('/auth/become-tenant', profileData.tenantProfile || {});
        logger.info("Tenant role activation API call successful.");
        userStateChanged = true; // Role added, state needs refresh
      } else if (!profileData.isTenant && user.isTenant) { // Deactivate Tenant
        logger.info("Attempting to deactivate Tenant role...");
        await api.delete('/auth/remove-tenant-role'); // Call the new DELETE endpoint
        logger.info("Tenant role deactivation API call successful.");
        userStateChanged = true; // Role removed, state needs refresh
      } else if (profileData.isTenant && user.isTenant) { // Update Existing Tenant Profile
        logger.info("Attempting to update existing Tenant profile details...");
        await api.patch('/auth/update-tenant-profile', profileData.tenantProfile || {});
        logger.info("Tenant profile details update API call successful.");
        // No need to set userStateChanged = true here unless the PATCH returns the user or affects roles/perms
      } else {
        logger.debug("No change in Tenant role status.");
      }

      // --- Handle Owner Role ---
      if (profileData.isOwner && !user.isOwner) { // Activate Owner
        logger.info("Attempting to activate Owner role...");
        await api.post('/auth/become-owner', profileData.ownerProfile || {});
        logger.info("Owner role activation API call successful.");
        userStateChanged = true; // Role added, state needs refresh
      } else if (!profileData.isOwner && user.isOwner) { // Deactivate Owner
        logger.info("Attempting to deactivate Owner role...");
        await api.delete('/auth/remove-owner-role'); // Call the new DELETE endpoint
        logger.info("Owner role deactivation API call successful.");
        userStateChanged = true; // Role removed, state needs refresh
      } else if (profileData.isOwner && user.isOwner) { // Update Existing Owner Profile
        logger.info("Attempting to update existing Owner profile details...");
        await api.patch('/auth/update-owner-profile', profileData.ownerProfile || {});
        logger.info("Owner profile details update API call successful.");
        // No need to set userStateChanged = true here
      } else {
        logger.debug("No change in Owner role status.");
      }

      // 4. Fetch updated user data ONLY if a role change or significant profile update happened
      if (userStateChanged) {
        logger.info("User state potentially changed, fetching updated user data from /auth/me...");
        const userResponse = await api.get('/auth/me'); // Fetch the LATEST state
        const updatedUser = userResponse.data.user;
        logger.debug("Fetched updated user data:", updatedUser);

        // 5. Update local state and storage
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        setUser(updatedUser); // Update context state
        logger.info("Local user state and storage updated.");

        // 6. Refresh permissions (roles definitely changed if activation/deactivation happened)
        logger.info("Refreshing permission cache...");
        await refreshPermissionCache();
        setPermissionsLoaded(true); // Ensure this is set
        logger.info("Permission cache refreshed.");
      } else {
        logger.info("No role activation/deactivation detected, skipping final user fetch and permission refresh.");
        // If only basic profile info changed, update state from the initial PATCH response
        const basicUpdatedUser = baseProfileResponse.data.user;
        localStorage.setItem('userData', JSON.stringify(basicUpdatedUser));
        setUser(basicUpdatedUser);
        logger.info("Updated user state with basic profile changes.");
      }

      logger.info('updateUserProfile completed successfully.');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile';
      logger.error(`Error updating user profile: ${message}`, error.response || error);
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false); // Stop loading indicator
    }
  };
  // --- END MODIFIED updateUserProfile ---

  const updateUserPreferences = async (preferences) => {
    try {
      setLoading(true);
      setError(null);
      
      // Appeler l'endpoint de mise à jour des préférences
      const response = await api.patch('/auth/update-preferences', { preferences });
      
      const updatedUser = response.data.user;
      
      // Mettre à jour les données utilisateur dans le localStorage et l'état
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour des préférences:', error);
      const message = 
        error.response?.data?.message || 
        'Une erreur est survenue lors de la mise à jour des préférences';
      
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const updateProfilePicture = async (formData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Sending profile picture update request...');
      const response = await api.post('/auth/update-profile-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Profile picture update response:', response.data);
      const updatedUser = response.data.user;
      console.log('Updated user data:', updatedUser);

      // Créer un nouvel objet pour forcer le re-rendu
      const newUserState = { ...updatedUser };
      
      // Mettre à jour les données utilisateur dans le localStorage et l'état
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      return { success: true };
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la photo de profil:', error);
      const message = 
        error.response?.data?.message || 
        'Une erreur est survenue lors de la mise à jour de la photo de profil';
      
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Vérifier si l'utilisateur a un rôle spécifique
   * @param {string} roleName - Nom du rôle à vérifier
   * @returns {boolean} - True si l'utilisateur a le rôle
   */
  const hasRole = (roleName) => {
    if (!user) return false;
    
    // Vérifier les rôles spéciaux
    if (roleName === 'admin' && user.isAdmin) return true;
    if (roleName === 'owner' && user.isOwner) return true;
    if (roleName === 'tenant' && user.isTenant) return true;
    
    // Vérifier les rôles dynamiques
    return user.roles?.includes(roleName) || false;
  };

  /**
   * Vérifier si l'utilisateur a une permission spécifique
   * @param {string} permissionName - Nom de la permission à vérifier
   * @returns {Promise<boolean>} - True si l'utilisateur a la permission
   */
  const hasPermission = async (permissionName) => {
    if (!user || !token) return false;
    
    try {
      // Vérifier si l'utilisateur est admin (a toutes les permissions)
      if (user.isAdmin) return true;
      
      // Vérifier la permission spécifique
      return await userHasPermission(user.id, permissionName);
    } catch (error) {
      console.error(`Error checking permission ${permissionName}:`, error);
      return false;
    }
  };

  // Stabiliser l'objet de valeur du contexte pour éviter les re-renders
  const contextValue = useMemo(() => ({
    user,
    token,
    loading,
    authLoading,
    error,
    login,
    register,
    logout,
    updateUserProfile,
    updateProfilePicture,
    updateUserPreferences,
    refreshUserData,
    requiresEmailVerification,
    emailToVerify,
    updateAuthState,
    isAuthenticated: !!token && !requiresEmailVerification,
    permissionsLoaded,
    isInitialized,
    hasRole,
    hasPermission
  }), [
    user,
    token,
    loading,
    authLoading,
    error,
    requiresEmailVerification,
    emailToVerify,
    permissionsLoaded,
    isInitialized,
    login,
    register,
    logout,
    updateUserProfile,
    updateProfilePicture,
    updateUserPreferences,
    refreshUserData,
    updateAuthState,
    hasRole,
    hasPermission
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};